import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SmoothCursor } from "@/components/ui/smooth-cursor";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import LocationProtectedRoute from "@/components/LocationProtectedRoute";
import { useEffect, useState } from "react";
import { loadModels } from "@/services/faceService";

// Pages
import LoginPage from "@/pages/LoginPage";
import NotFound from "@/pages/NotFound";

// Admin Pages
import AdminLayout from "@/layouts/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminStaffPage from "@/pages/admin/AdminStaffPage";
import AdminTasksPage from "@/pages/admin/AdminTasksPage";
import AdminAttendancePage from "@/pages/admin/AdminAttendancePage";
import AdminAttendanceAnalyticsPage from "@/pages/admin/AdminAttendanceAnalyticsPage";
import AdminReportsPage from "@/pages/admin/AdminReportsPage";
import AdminExpensesPage from "@/pages/admin/AdminExpensesPage";
import AdminPaidLeavesPage from "@/pages/admin/AdminPaidLeavesPage";
import AdminVoicePage from "@/pages/admin/AdminVoicePage";
import AdminSpecialStaffPage from "@/pages/admin/AdminSpecialStaffPage";
import AdminSpecialStaffListPage from "@/pages/admin/AdminSpecialStaffListPage";
import AdminCategoriesPage from "@/pages/admin/AdminCategoriesPage";
import AdminPayoutsPage from "@/pages/admin/AdminPayoutsPage";

// Staff Pages
import StaffLayout from "@/layouts/StaffLayout";
import StaffTasksPage from "@/pages/staff/StaffTasksPage";
import StaffProfilePage from "@/pages/staff/StaffProfilePage";
import StaffWalletPage from "@/pages/staff/StaffWalletPage";
import StaffDailyReportPage from "@/pages/staff/StaffDailyReportPage";
import StaffAttendancePage from "@/pages/staff/StaffAttendancePage";
import StaffPaidLeavesPage from "@/pages/staff/StaffPaidLeavesPage";
import StaffTravelAllowancePage from "@/pages/staff/StaffTravelAllowancePage";
import StaffExpensesPage from "@/pages/staff/StaffExpensesPage";

// Public Pages
import AttendancePage from "@/pages/AttendancePage";

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    loadModels();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TooltipProvider>
            <SmoothCursor />
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Routes>
                {/* Redirect root to login */}
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="/login" element={<LoginPage />} />

                {/* Public Attendance Route - Protected by Location */}
                <Route
                  path="/attendance"
                  element={<AttendancePage />}
                />

                {/* Admin Routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute allowedRole="admin">
                      <AdminLayout>
                        <AdminDashboard />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/staff"
                  element={
                    <ProtectedRoute allowedRole="admin">
                      <AdminLayout>
                        <AdminStaffPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/staff/:staffId/special"
                  element={
                    <ProtectedRoute allowedRole="admin">
                      <AdminLayout>
                        <AdminSpecialStaffPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/tasks"
                  element={
                    <ProtectedRoute allowedRole="admin">
                      <AdminLayout>
                        <AdminTasksPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/attendance"
                  element={
                    <ProtectedRoute allowedRole="admin">
                      <AdminLayout>
                        <AdminAttendancePage />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/attendance/analytics"
                  element={
                    <ProtectedRoute allowedRole="admin">
                      <AdminLayout>
                        <AdminAttendanceAnalyticsPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/expenses"
                  element={
                    <ProtectedRoute allowedRole="admin">
                      <AdminLayout>
                        <AdminExpensesPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/paid-leaves"
                  element={
                    <ProtectedRoute allowedRole="admin">
                      <AdminLayout>
                        <AdminPaidLeavesPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/categories"
                  element={
                    <ProtectedRoute allowedRole="admin">
                      <AdminLayout>
                        <AdminCategoriesPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/voice"
                  element={
                    <ProtectedRoute allowedRole="admin">
                      <AdminLayout>
                        <AdminVoicePage />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/reports"
                  element={
                    <ProtectedRoute allowedRole="admin">
                      <AdminLayout>
                        <AdminReportsPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/payouts"
                  element={
                    <ProtectedRoute allowedRole="admin">
                      <AdminLayout>
                        <AdminPayoutsPage />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />


                {/* Staff Routes */}
                <Route
                  path="/staff"
                  element={
                    <ProtectedRoute allowedRole="staff">
                      <StaffLayout>
                        <StaffTasksPage />
                      </StaffLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staff/reports"
                  element={
                    <ProtectedRoute allowedRole="staff">
                      <StaffLayout>
                        <StaffDailyReportPage />
                      </StaffLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staff/attendance"
                  element={
                    <ProtectedRoute allowedRole="staff">
                      <StaffLayout>
                        <StaffAttendancePage />
                      </StaffLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/staff/wallet"
                  element={
                    <ProtectedRoute allowedRole="staff">
                      <StaffLayout>
                        <StaffWalletPage />
                      </StaffLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staff/paid-leaves"
                  element={
                    <ProtectedRoute allowedRole="staff">
                      <StaffLayout>
                        <StaffPaidLeavesPage />
                      </StaffLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staff/travel-allowance"
                  element={
                    <ProtectedRoute allowedRole="staff">
                      <StaffLayout>
                        <StaffTravelAllowancePage />
                      </StaffLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staff/expenses"
                  element={
                    <ProtectedRoute allowedRole="staff">
                      <StaffLayout>
                        <StaffExpensesPage />
                      </StaffLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staff/profile"
                  element={
                    <ProtectedRoute allowedRole="staff">
                      <StaffLayout>
                        <StaffProfilePage />
                      </StaffLayout>
                    </ProtectedRoute>
                  }
                />

                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
